import { COOKIE_GEO } from './constants';
import { getCookie } from './storage';
import type { SessionGeo } from './types';

/** Result of async visitor context resolution. */
export interface ResolvedContext {
  ipAddress: string | null;
  geo: SessionGeo | null;
}

/**
 * Resolve geo data synchronously from the mf_geo cookie set by the
 * CloudFront Function pair (ADR-0015). The cookie value is base64url-encoded
 * JSON containing country, region, city, postal_code, latitude, longitude,
 * timezone.
 *
 * Returns { ipAddress, geo } on success, null if cookie is absent or unparseable.
 */
export function resolveGeoFromCookie(): { ipAddress: string | null; geo: SessionGeo } | null {
  try {
    const raw = getCookie(COOKIE_GEO);
    if (!raw) return null;

    // Base64url → standard base64 → JSON string
    // Values may be percent-encoded by CloudFront (e.g. "Jo%C3%A3o Pessoa"),
    // we decode them below after JSON.parse.
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const data = JSON.parse(json);

    // Decode percent-encoded string values (safe for all languages)
    for (const key of Object.keys(data)) {
      if (typeof data[key] === 'string') {
        try { data[key] = decodeURIComponent(data[key]); } catch { /* keep as-is */ }
      }
    }

    if (!data || typeof data !== 'object' || typeof data.country !== 'string') {
      return null;
    }

    // address field is "ip:port" — extract IP
    let ipAddress: string | null = null;
    if (typeof data.address === 'string' && data.address) {
      ipAddress = data.address.split(':')[0] || null;
    }

    const geo: SessionGeo = {
      country: data.country,
      ...(data.country_name && { country_name: String(data.country_name) }),
      ...(data.region && { state_code: String(data.region), region: String(data.region) }),
      ...(data.region_name && { state_name: String(data.region_name), region_name: String(data.region_name) }),
      ...(data.city && { city: String(data.city) }),
      ...(data.postal_code && { postal_code: String(data.postal_code) }),
      ...(data.metro_code && { metro_code: String(data.metro_code) }),
      ...(data.latitude && { latitude: String(data.latitude) }),
      ...(data.longitude && { longitude: String(data.longitude) }),
      ...(data.timezone && { timezone: String(data.timezone) }),
      ...(data.asn && { asn: String(data.asn) }),
    };

    return { ipAddress, geo };
  } catch {
    return null;
  }
}

/**
 * Resolve visitor IP synchronously from the legacy `window.MF.ipAddress` global
 * set by the `cms-macros` render-time geoip-init macros on legacy CMS pages.
 * Returns null when the global is absent or not a non-empty string.
 */
export function resolveIpFromLegacyGlobal(): string | null {
  try {
    const mf = (window as { MF?: { ipAddress?: unknown } }).MF;
    if (mf && typeof mf.ipAddress === 'string' && mf.ipAddress) {
      return mf.ipAddress;
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Resolve visitor geo synchronously from the legacy `window.MF.geoData` global
 * set by the `cms-macros` render-time geoip-init macros on legacy CMS pages.
 *
 * The global uses MaxMind-style field names (`subdivision_1_iso_code`,
 * `subdivision_1_name`, `city_name`, `country`, `postal_code`). Translates
 * them to SessionGeo's field naming to match the mf_geo cookie mapping.
 *
 * Returns null when the global is absent, not an object, or carries no usable
 * fields.
 */
export function resolveGeoFromLegacyGlobal(): SessionGeo | null {
  try {
    const mf = (window as { MF?: { geoData?: unknown } }).MF;
    const data = mf && mf.geoData;
    if (!data || typeof data !== 'object') return null;

    const d = data as Record<string, unknown>;
    const str = (v: unknown): string | undefined =>
      typeof v === 'string' && v ? v : undefined;

    const geo: SessionGeo = {
      ...(str(d.country) && { country: str(d.country)! }),
      ...(str(d.country_name) && { country_name: str(d.country_name)! }),
      ...(str(d.subdivision_1_iso_code) && {
        state_code: str(d.subdivision_1_iso_code)!,
        region: str(d.subdivision_1_iso_code)!,
      }),
      ...(str(d.subdivision_1_name) && {
        state_name: str(d.subdivision_1_name)!,
        region_name: str(d.subdivision_1_name)!,
      }),
      ...(str(d.city_name) && { city: str(d.city_name)! }),
      ...(str(d.postal_code) && { postal_code: str(d.postal_code)! }),
    };

    return Object.keys(geo).length > 0 ? geo : null;
  } catch {
    return null;
  }
}

/**
 * Resolve visitor IP address and geo data from a remote endpoint.
 *
 * The endpoint is expected to return JSON with at least:
 *   { ip: string, postal_code?: string, state_code?: string,
 *     state_name?: string, city?: string, country?: string }
 *
 * Returns nulls on timeout, network error, or invalid response.
 */
export async function resolveVisitorContext(
  geoEndpoint: string,
  timeoutMs: number,
): Promise<ResolvedContext> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(geoEndpoint, {
      signal: controller.signal,
      credentials: 'omit',
    });

    if (!response.ok) {
      return { ipAddress: null, geo: null };
    }

    const data = await response.json();

    const ipAddress: string | null =
      typeof data.ip === 'string' && data.ip ? data.ip : null;

    const geo: SessionGeo | null = ipAddress
      ? {
          ...(data.postal_code && { postal_code: String(data.postal_code) }),
          ...(data.state_code && { state_code: String(data.state_code) }),
          ...(data.state_name && { state_name: String(data.state_name) }),
          ...(data.city && { city: String(data.city) }),
          ...(data.country && { country: String(data.country) }),
        }
      : null;

    return { ipAddress, geo };
  } catch {
    // Timeout (AbortError), network error, JSON parse error — all return nulls
    return { ipAddress: null, geo: null };
  } finally {
    clearTimeout(timer);
  }
}
